import { signalVisual } from "@healthos/shared";
import { CalendarBlank, type Icon as PhosphorIcon, Waveform } from "@phosphor-icons/react";
import { phosphorByName } from "../../icons/phosphor-by-name";
import { cn } from "../../lib/cn";
import { AttentionPanel, PANEL_ROW, panelRowStyle } from "./panel";
import { describeWatchingItem } from "./signal-metrics";
import type { AttentionWatchingItem, OpacityMix } from "./types";

export function AttentionSignals({
  label,
  opacityMix,
  watching,
}: {
  label?: string;
  opacityMix: OpacityMix;
  watching: AttentionWatchingItem[];
}) {
  // Metric readings render as the same quiet label/value rows as plain items
  // on every card class; the tile-and-slider treatment read as a dashboard
  // dropped into an editorial card.
  // The signal kind survives the metric rewrite, or the row it describes loses
  // its icon on the way through.
  const rows: AttentionWatchingItem[] = watching.map((row) => {
    const item = describeWatchingItem(row);
    if (item.kind === "plain") return item.row;
    const value = `${item.metric.value}${item.metric.unit ? ` ${item.metric.unit}` : ""}`;
    return row.kind
      ? { label: item.metric.label, value, kind: row.kind }
      : { label: item.metric.label, value };
  });

  if (rows.length === 0) return null;

  return (
    <AttentionPanel label={label ?? "Signals we're watching"} opacityMix={opacityMix}>
      <PlainSignalsList opacityMix={opacityMix} rows={rows} />
    </AttentionPanel>
  );
}

function PlainSignalsList({
  opacityMix,
  rows,
}: {
  opacityMix: OpacityMix;
  rows: AttentionWatchingItem[];
}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {rows.map((row) => {
        const Icon = iconForWatchingRow(row);
        return (
          <li
            key={row.label}
            className={cn("flex items-center justify-between gap-3", PANEL_ROW)}
            style={panelRowStyle}
          >
            <span
              className={cn("flex min-w-0 items-center gap-2.5 font-sans font-semibold text-sm")}
              style={{ color: opacityMix(92) }}
            >
              <span
                aria-hidden="true"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-md border"
                style={{
                  background: "var(--attn-signal-tile-bg)",
                  borderColor: "var(--attn-signal-tile-border)",
                  color: "var(--attn-accent)",
                }}
              >
                <Icon size={16} weight="duotone" />
              </span>
              <span className="min-w-0 truncate">{row.label}</span>
            </span>
            <span
              className="whitespace-nowrap text-right font-mono text-sm tabular-nums"
              style={{ color: opacityMix(96) }}
            >
              {row.value}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// Every row gets one: a blank where its neighbours have icons reads as a bug.
// The glyph comes from the shared signal-visual map, keyed on the signal kind
// rather than on the label a member reads, so a kind added there is drawn here
// without a second list to keep in step. Rows with no signal behind them are
// the plan and focus-area fallbacks.
function iconForWatchingRow(row: AttentionWatchingItem): PhosphorIcon {
  if (row.icon) return phosphorByName(row.icon);
  if (!row.kind) return Waveform;
  // The two rows a card cites that are reads on the calendar rather than signal
  // kinds, so signal-visual has nothing to say about them. `calendar.event` is
  // a kind and resolves through the map like everything else.
  if (row.kind === "calendar.gap" || row.kind === "calendar.load") return CalendarBlank;
  return phosphorByName(signalVisual(row.kind).icon);
}
